import { useEffect, useRef, useState } from "react";
import { DropZone } from "@/components/DropZone";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileTextIcon as FileText, ImageIcon as ImageIcon, TrashIcon as Trash, UploadSimpleIcon as UploadSimple, FloppyDiskIcon as FloppyDisk, BuildingsIcon as Buildings, TextTIcon as TextT, ToggleLeftIcon as ToggleLeft, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown } from "@phosphor-icons/react";

const MOSTRA_LABELS = {
  logo: "Mostra logo principale", footer_logos: "Mostra loghi footer",
  richiesta: "Mostra richiesta intervento", descrizione: "Mostra descrizione attività",
  note_interne: "Mostra note interne (privato)", km: "Mostra km percorsi",
  firma: "Mostra area firma", legali: "Mostra testi GDPR / dichiarazione", numero_pagina: "Mostra numero pagina",
};
const ETICHETTE_LABELS = {
  intervento_n: "Intervento n.", data_richiesta: "Data richiesta", cliente: "Cliente", piva: "P.Iva",
  via: "Via", cap: "CAP", citta: "Città", provincia: "Provincia", telefono: "Telefono",
  tipo_intervento: "Tipo intervento", richiesta: "Richiesta", descrizione: "Descrizione",
  ore_tecnici: "Titolo tabella ore", tecnico: "Tecnico", orario: "Orario", ore_lavorate: "Ore lavorate", km: "Km percorsi",
};

export function ReportTemplateSection() {
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previews, setPreviews] = useState({});
  const logoRef = useRef(null);
  const footerRef = useRef(null);

  const load = () => api.get("/interventi-config/report-template").then(({ data }) => setTpl(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Miniature via proxy autenticato (object storage privato) → object URL
  const footerKey = (tpl?.footer_logos || []).map((f) => f.id).join(",");
  const logoKey = tpl?.logo?.object_key || "";
  useEffect(() => {
    if (!tpl) return;
    let cancelled = false;
    const urls = {};
    const fetchOne = async (mapKey, path) => {
      try {
        const res = await api.get(path, { responseType: "blob" });
        if (cancelled) return;
        urls[mapKey] = window.URL.createObjectURL(res.data);
      } catch { /* placeholder */ }
    };
    (async () => {
      const jobs = [];
      if (tpl.logo?.object_key) jobs.push(fetchOne("__logo__", "/interventi-config/report-template/logo/preview"));
      for (const f of tpl.footer_logos || []) jobs.push(fetchOne(f.id, `/interventi-config/report-template/footer-logos/${f.id}/preview`));
      await Promise.all(jobs);
      if (!cancelled) setPreviews(urls);
    })();
    return () => {
      cancelled = true;
      Object.values(urls).forEach((u) => window.URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoKey, footerKey]);

  if (!tpl) return null;

  const setAz = (k, v) => setTpl((t) => ({ ...t, azienda: { ...t.azienda, [k]: v } }));
  const setMostra = (k, v) => setTpl((t) => ({ ...t, mostra: { ...t.mostra, [k]: v } }));
  const setEtic = (k, v) => setTpl((t) => ({ ...t, etichette: { ...t.etichette, [k]: v } }));
  const setMar = (k, v) => setTpl((t) => ({ ...t, margini: { ...t.margini, [k]: Number(v) || 0 } }));
  const set = (k, v) => setTpl((t) => ({ ...t, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/interventi-config/report-template", {
        azienda: tpl.azienda, titolo: tpl.titolo, testo_gdpr: tpl.testo_gdpr,
        testo_dichiarazione: tpl.testo_dichiarazione, testo_firma: tpl.testo_firma,
        etichette: tpl.etichette, mostra: tpl.mostra, margini: tpl.margini,
      });
      setTpl(data);
      toast.success("Template report salvato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/interventi-config/report-template/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setTpl((t) => ({ ...t, logo: data.logo })); toast.success("Logo caricato"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { if (logoRef.current) logoRef.current.value = ""; }
  };
  const removeLogo = async () => { try { await api.delete("/interventi-config/report-template/logo"); setTpl((t) => ({ ...t, logo: null })); toast.success("Logo rimosso"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const uploadFooter = async (files) => {
    if (!files || !files.length) return;
    const fd = new FormData(); for (const f of files) fd.append("files", f);
    try { const { data } = await api.post("/interventi-config/report-template/footer-logos", fd, { headers: { "Content-Type": "multipart/form-data" } }); setTpl((t) => ({ ...t, footer_logos: data.footer_logos })); toast.success("Logo footer caricato"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { if (footerRef.current) footerRef.current.value = ""; }
  };
  const removeFooter = async (id) => { try { const { data } = await api.delete(`/interventi-config/report-template/footer-logos/${id}`); setTpl((t) => ({ ...t, footer_logos: data.footer_logos })); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const moveFooter = async (index, dir) => {
    const arr = [...(tpl.footer_logos || [])];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setTpl((t) => ({ ...t, footer_logos: arr }));
    try {
      const { data } = await api.put("/interventi-config/report-template/footer-logos/reorder", { order: arr.map((f) => f.id) });
      setTpl((t) => ({ ...t, footer_logos: data.footer_logos }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  const AZ = [["ragione_sociale", "Ragione sociale"], ["indirizzo", "Indirizzo"], ["cap", "CAP"], ["citta", "Città"], ["provincia", "Provincia"], ["piva", "Partita IVA"], ["telefono", "Telefono"], ["email", "Email"], ["sito_web", "Sito web (opz.)"]];

  return (
    <Card className="rounded-sm border-border shadow-none p-6" data-testid="report-template-section">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><FileText size={20} weight="bold" className="text-primary" /></div>
        <div>
          <h3 className="font-heading font-semibold">Template report intervento</h3>
          <p className="text-xs text-muted-foreground">Personalizza il PDF "Stampa intervento": dati azienda, loghi, testi, etichette e sezioni visibili.</p>
        </div>
      </div>

      {/* Dati azienda */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2 text-sm font-medium"><Buildings size={16} /> Dati azienda stampati</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {AZ.map(([k, lbl]) => (
            <div key={k}>
              <Label className="text-[10px] uppercase tracking-wider">{lbl}</Label>
              <Input value={tpl.azienda?.[k] || ""} onChange={(e) => setAz(k, e.target.value)} className="mt-1 rounded-sm h-9" data-testid={`rt-az-${k}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Loghi */}
      <div className="mb-6 grid md:grid-cols-2 gap-4">
        <div className="border border-border rounded-sm p-3">
          <div className="flex items-center gap-2 mb-2 text-sm font-medium"><ImageIcon size={16} /> Logo principale</div>
          {tpl.logo ? (
            <div className="flex items-center justify-between gap-2 text-sm bg-secondary/40 rounded-sm px-2 py-1.5" data-testid="rt-logo-current">
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-9 w-14 rounded-sm bg-white border border-border flex items-center justify-center overflow-hidden shrink-0">
                  {previews.__logo__ ? <img src={previews.__logo__} alt="logo" className="max-h-8 max-w-full object-contain" data-testid="rt-logo-thumb" /> : <ImageIcon size={16} className="text-muted-foreground" />}
                </div>
                <span className="truncate">{tpl.logo.nome}</span>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={removeLogo} data-testid="rt-logo-remove"><Trash size={14} /></Button>
            </div>
          ) : <p className="text-xs text-muted-foreground mb-2">Nessun logo caricato.</p>}
          <DropZone onFiles={(fl) => uploadLogo(fl?.[0])} className="block">
          <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="rt-logo-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
          <Button variant="outline" size="sm" className="rounded-sm gap-1 mt-2" onClick={() => logoRef.current?.click()} data-testid="rt-logo-upload"><UploadSimple size={14} /> Carica logo</Button>
          </DropZone>
        </div>
        <div className="border border-border rounded-sm p-3">
          <div className="flex items-center gap-2 mb-2 text-sm font-medium"><ImageIcon size={16} /> Loghi footer / certificazioni</div>
          <div className="space-y-1 mb-2" data-testid="rt-footer-list">
            {(tpl.footer_logos || []).length === 0 && <p className="text-xs text-muted-foreground">Nessun logo footer.</p>}
            {(tpl.footer_logos || []).map((f, i) => (
              <div key={f.id} className="flex items-center justify-between gap-2 text-sm bg-secondary/40 rounded-sm px-2 py-1" data-testid={`rt-footer-${f.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] text-muted-foreground w-4 text-center shrink-0" data-testid={`rt-footer-pos-${f.id}`}>{i + 1}</span>
                  <div className="h-8 w-12 rounded-sm bg-white border border-border flex items-center justify-center overflow-hidden shrink-0">
                    {previews[f.id] ? <img src={previews[f.id]} alt={f.nome} className="max-h-7 max-w-full object-contain" data-testid={`rt-footer-thumb-${f.id}`} /> : <ImageIcon size={14} className="text-muted-foreground" />}
                  </div>
                  <span className="truncate">{f.nome}</span>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button variant="ghost" size="icon" className="h-6 w-6" disabled={i === 0} onClick={() => moveFooter(i, -1)} data-testid={`rt-footer-up-${f.id}`}><CaretUp size={13} /></Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6" disabled={i === (tpl.footer_logos.length - 1)} onClick={() => moveFooter(i, 1)} data-testid={`rt-footer-down-${f.id}`}><CaretDown size={13} /></Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeFooter(f.id)} data-testid={`rt-footer-remove-${f.id}`}><Trash size={13} /></Button>
                </div>
              </div>
            ))}
          </div>
          <DropZone onFiles={(fl) => uploadFooter(Array.from(fl))} className="block">
          <input ref={footerRef} type="file" accept="image/*" multiple className="hidden" data-testid="rt-footer-input" onChange={(e) => uploadFooter(Array.from(e.target.files || []))} />
          <Button variant="outline" size="sm" className="rounded-sm gap-1" onClick={() => footerRef.current?.click()} data-testid="rt-footer-upload"><UploadSimple size={14} /> Aggiungi logo footer</Button>
          </DropZone>
        </div>
      </div>

      {/* Testi */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2 text-sm font-medium"><TextT size={16} /> Testi configurabili</div>
        <div className="space-y-3">
          <div><Label className="text-[10px] uppercase tracking-wider">Titolo report</Label><Input value={tpl.titolo || ""} onChange={(e) => set("titolo", e.target.value)} className="mt-1 rounded-sm h-9" data-testid="rt-titolo" /></div>
          <div><Label className="text-[10px] uppercase tracking-wider">Testo GDPR</Label><Textarea value={tpl.testo_gdpr || ""} onChange={(e) => set("testo_gdpr", e.target.value)} className="mt-1 rounded-sm text-sm" rows={2} data-testid="rt-gdpr" /></div>
          <div><Label className="text-[10px] uppercase tracking-wider">Dichiarazione lavori</Label><Textarea value={tpl.testo_dichiarazione || ""} onChange={(e) => set("testo_dichiarazione", e.target.value)} className="mt-1 rounded-sm text-sm" rows={2} data-testid="rt-dichiarazione" /></div>
          <div><Label className="text-[10px] uppercase tracking-wider">Testo firma</Label><Input value={tpl.testo_firma || ""} onChange={(e) => set("testo_firma", e.target.value)} className="mt-1 rounded-sm h-9" data-testid="rt-firma" /></div>
        </div>
      </div>

      {/* Etichette */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2 text-sm font-medium"><TextT size={16} /> Etichette</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(ETICHETTE_LABELS).map(([k, lbl]) => (
            <div key={k}>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{lbl}</Label>
              <Input value={tpl.etichette?.[k] || ""} onChange={(e) => setEtic(k, e.target.value)} className="mt-0.5 rounded-sm h-8 text-sm" data-testid={`rt-etic-${k}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Mostra/Nascondi */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2 text-sm font-medium"><ToggleLeft size={16} /> Sezioni visibili</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(MOSTRA_LABELS).map(([k, lbl]) => (
            <div key={k} className="flex items-center justify-between border border-border rounded-sm px-3 py-1.5">
              <span className="text-sm">{lbl}</span>
              <Switch checked={!!tpl.mostra?.[k]} onCheckedChange={(v) => setMostra(k, v)} data-testid={`rt-mostra-${k}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Margini */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2 text-sm font-medium">Margini (mm)</div>
        <div className="grid grid-cols-4 gap-2 max-w-md">
          {["top", "bottom", "left", "right"].map((k) => (
            <div key={k}><Label className="text-[10px] uppercase tracking-wider">{k}</Label><Input type="number" min="0" value={tpl.margini?.[k] ?? 15} onChange={(e) => setMar(k, e.target.value)} className="mt-1 rounded-sm h-9" data-testid={`rt-mar-${k}`} /></div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-white/95 backdrop-blur border-t border-border flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-sm gap-2" data-testid="rt-save-btn"><FloppyDisk size={16} weight="bold" /> {saving ? "Salvataggio…" : "Salva template"}</Button>
      </div>
    </Card>
  );
}
